import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Folder, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { api, errorMessage } from '@/lib/api';
import { Button } from './ui/button';
import { Input } from './ui/form';
import { ErrorAlert } from './ui/misc';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

export type CharacterFolder = { id: string; title: string };
export const useCharacterFolders = () => useQuery({ queryKey: ['character-folders'], queryFn: () => api<CharacterFolder[]>('/api/characterFolders') });

export function CharacterFolders({ folderId, onChange, allLabel = 'All folders' }: { folderId: string; onChange: (id: string) => void; allLabel?: string }) {
  const q = useCharacterFolders();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const add = useMutation({
    mutationFn: () => api<CharacterFolder>('/api/characterFolders', { method: 'POST', body: { title: name.trim() } }),
    onSuccess: async (f) => { await qc.invalidateQueries({ queryKey: ['character-folders'] }); onChange(f.id); setOpen(false); setName(''); },
    onError: (e) => toast.error(errorMessage(e)),
  });
  return <div className="space-y-2">
    <div className="flex flex-wrap gap-2">
      <Button type="button" size="sm" variant={!folderId ? 'secondary' : 'outline'} onClick={() => onChange('')}>{allLabel}</Button>
      {q.data?.map((f) => <Button type="button" key={f.id} size="sm" className="max-w-full" variant={folderId === f.id ? 'secondary' : 'outline'} onClick={() => onChange(f.id)}><Folder /><span className="truncate">{f.title}</span></Button>)}
      <Button type="button" size="sm" variant="outline" onClick={() => setOpen(true)}><Plus /> New folder</Button>
    </div>
    {q.isLoading && <p className="text-sm">Loading folders…</p>}
    {q.error && <ErrorAlert message={errorMessage(q.error)} />}
    <Dialog open={open} onOpenChange={setOpen}><DialogContent>
      <DialogHeader><DialogTitle>New character folder</DialogTitle><DialogDescription>Create a folder for related characters.</DialogDescription></DialogHeader>
      <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); if (name.trim() && !add.isPending) add.mutate(); }}>
        <Input aria-label="Folder name" autoFocus maxLength={100} placeholder="Folder name" value={name} onChange={(e) => setName(e.target.value)} />
        <DialogFooter><Button type="submit" disabled={!name.trim()} loading={add.isPending}>Create folder</Button></DialogFooter>
      </form>
    </DialogContent></Dialog>
  </div>;
}
